/**
 * Live lessons rule 9: every coach and player pair has one permanent room,
 * room_<coachId>_<playerId>. A lesson is a time on it. Joining goes through
 * joinLiveRoom: inside a lesson's window it is that lesson, and outside
 * every window it is a call that connects only once both sides accept,
 * the other from the thread. A conversation's own call keeps conv-{id}.
 */

/** The pair's permanent room, from a booking or the pair. */
export function pairRoomId(b: { roomId?: string | null; coachId: string; playerId: string }): string {
  return b.roomId?.startsWith('room_') ? b.roomId : `room_${b.coachId}_${b.playerId}`;
}

/** The room a call signals on. */
export function callRoomId(p: {
  roomId?: string;
  bookingId?: string;
  booking?: { roomId?: string | null; coachId: string; playerId: string };
  conversationId?: string;
}): string {
  if (p.roomId) return p.roomId;
  if (p.booking) return pairRoomId(p.booking);
  return `conv-${p.conversationId ?? ''}`;
}

/** The caller's side of a pair's room, and the other side. */
export function sideOfRoom(roomId: string, uid: string):
  { side: 'coach' | 'player'; otherId: string } | null {
  if (!roomId.startsWith('room_')) return null;
  const rest = roomId.slice('room_'.length);
  if (rest.startsWith(`${uid}_`)) return { side: 'coach', otherId: rest.slice(uid.length + 1) };
  if (rest.endsWith(`_${uid}`)) return { side: 'player', otherId: rest.slice(0, rest.length - uid.length - 1) };
  return null;
}

/** What joinLiveRoom returns. */
export type RoomJoin =
  | { kind: 'lesson'; bookingId: string; recorded: true; joinedAt: string }
  | { kind: 'guest'; bookingId: string; recorded: false }
  | { kind: 'call'; recorded: false; connected: boolean };

/** Connect now, or wait for the other side to accept the call. */
export function joinGate(r: RoomJoin): 'connect' | 'wait' {
  return r.kind !== 'call' || r.connected ? 'connect' : 'wait';
}

/** The room must stay open at least this long past now for the handshake to go first. */
export const OPEN_MARGIN_MS = 60_000;

/**
 * The pair's room is already open for signalling to this person (the rules'
 * roomOpenFor: liveRooms/{roomId} open until later, and they are its coach,
 * player or a guest). Then the handshake starts at once and joinLiveRoom
 * runs beside it, rather than first: on staging a cold joinLiveRoom held
 * both sides for 5 s before any connection (QA's walk of 29 October).
 * Pure, so tested.
 */
export function roomOpenFor(
  room: { openUntil?: unknown; coachId?: unknown; playerId?: unknown; guests?: unknown } | null | undefined,
  uid: string,
  nowMs: number,
): boolean {
  if (!room) return false;
  const until = room.openUntil as { toMillis?: () => number } | string | null | undefined;
  const ms = typeof until === 'string' ? Date.parse(until)
    : until && typeof until.toMillis === 'function' ? until.toMillis() : NaN;
  if (!(ms > nowMs + OPEN_MARGIN_MS)) return false;
  return room.coachId === uid || room.playerId === uid || (Array.isArray(room.guests) && room.guests.includes(uid));
}
